import { Amplify } from "aws-amplify";
import {
  signOut,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  type AuthUser,
} from "@aws-amplify/auth";
import { defaultStorage } from "@aws-amplify/core";
import cognitoConfig from "@/cognito.config";
import { ref } from "vue";
import { isOAuthSignInAsync } from "./CognitoAuthStorage";

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      //region: cognitoConfig.cognito.region,
      userPoolId: cognitoConfig.cognito.userPoolId,
      userPoolClientId: cognitoConfig.cognito.clientId,
      loginWith: {
        oauth: {
          domain: cognitoConfig.cognito.domain,
          scopes: cognitoConfig.cognito.scope,
          redirectSignIn: [cognitoConfig.cognito.redirectUri],
          redirectSignOut: [cognitoConfig.cognito.logoutUri],
          responseType: cognitoConfig.cognito.responseType as "code" | "token",
        },
        username: true,
        email: false,
        phone: false,
      },
    },
  },
});

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | undefined | null;
  idToken: string | undefined | null;
  user: AuthUser | null;
}

class CognitoAuthManager {
  private authState = ref<AuthState>({
    isAuthenticated: false,
    accessToken: null,
    idToken: null,
    user: null,
  });
  private subscribers: ((authState: any) => void)[] = [];
  private initialized = false;

  async initializeAsync() {
    try {
      if (this.initialized) return;
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken;
      // Ensure username is set from ID token if available
      const userName =
        (idToken?.payload["custom:app_username"] as string) || user?.username;
      user.username = userName;
      this.authState.value = {
        isAuthenticated: true,
        accessToken: session?.tokens?.accessToken?.toString(),
        idToken: idToken?.toString(),
        user: user,
      };
      console.log("Cognito Auth initialized:", this.authState.value);
    } catch (error) {
      this.authState.value = {
        isAuthenticated: false,
        accessToken: null,
        idToken: null,
        user: null,
      };
    }
    this.initialized = true;
    this.notifySubscribers();
  }

  private notifySubscribers() {
    this.subscribers.forEach((callback) => callback(this.authState));
  }

  // Subscribe to auth state changes
  subscribe(callback: (authState: any) => void) {
    this.subscribers.push(callback);
    // Immediately call with current state
    callback(this.authState);
  }

  // Unsubscribe from auth state changes
  unsubscribe(callback?: (authState: any) => void) {
    if (callback) {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    } else {
      this.subscribers = [];
    }
  }

  getAuthState() {
    return this.authState;
  }

  // Sign in using hosted UI
  async signIn() {
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error("Cognito sign in error:", error);
      throw error;
    }
  }

  // Sign in using hosted UI with Okta
  async signInWithOkta() {
    try {
      await signInWithRedirect({
        provider: { custom: cognitoConfig.cognito.oktaOidcProvider },
      });
    } catch (error) {
      console.error("Cognito sign in with okta error:", error);
      throw error;
    }
  }

  async signOut() {
    try {
      // Get isOAuth before signing out clears storage
      const isOAuth = await isOAuthSignInAsync(defaultStorage, cognitoConfig);
      await signOut();
      this.authState.value = {
        isAuthenticated: false,
        accessToken: null,
        idToken: null,
        user: null,
      };
      this.notifySubscribers();
      // non-OAuth sign-outs need to be redirected to logout URL
      if (!isOAuth) window.location.href = cognitoConfig.cognito.logoutUri;
    } catch (error) {
      console.error("Cognito sign out error:", error);
      throw error;
    }
  }

  async getUser() {
    try {
      const user = await getCurrentUser();
      const userInfo = await fetchUserAttributes();
      return {
        preferred_username: userInfo?.username || user.username,
        email: userInfo?.email,
        name: userInfo?.name,
        attributes: userInfo.attributes,
      };
    } catch (error) {
      console.error("Error getting Cognito user:", error);
      return null;
    }
  }

  async getAccessToken() {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error("Error getting access token:", error);
      return null;
    }
  }

  async refreshSession() {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      this.authState.value.accessToken =
        session?.tokens?.accessToken?.toString() || null;
      this.authState.value.idToken =
        session?.tokens?.idToken?.toString() || null;
      this.authState.value.isAuthenticated = !!this.authState.value.accessToken;
      this.notifySubscribers();
    } catch (error) {
      console.error("Error refreshing Cognito session:", error);
      throw error;
    }
  }

  async getIdToken() {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error("Error getting ID token:", error);
      return null;
    }
  }

  async getLoginTime() {
    try {
      const session = await fetchAuthSession();
      return Number(session?.tokens?.idToken?.payload?.auth_time) || null;
    } catch (error) {
      console.error("Error getting login time:", error);
      return null;
    }
  }

  // Handle login redirect (called after successful OAuth callback)
  async handleLoginRedirect() {
    try {
      await this.initializeAsync();
      return this.authState;
    } catch (error) {
      console.error("Error handling login redirect:", error);
      throw error;
    }
  }

  async isAuthenticated() {
    return (await this.getIdToken()) !== null;
  }

  setOriginalUri(uri: string) {
    sessionStorage.setItem("cognito_original_uri", uri);
  }

  getOriginalUri() {
    return sessionStorage.getItem("cognito_original_uri");
  }

  removeOriginalUri() {
    sessionStorage.removeItem("cognito_original_uri");
  }
}

const cognitoAuthManager = new CognitoAuthManager();

export default cognitoAuthManager;
